import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { config, useSpringValue } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import { mobileDrawerCloseSpring, mobileDrawerOpenSpring, mobileDrawerOverdragResistance } from "@catchy/app-core";
import { updateDrawerBackgroundInteractivity } from "./drawerAccessibility";
export { updateDrawerBackgroundInteractivity };
const mobileDrawerSnapVelocityThreshold = 0.3;
const mobileDrawerSnapIntentBuffer = 32;
const mobileDrawerScrollEdgeTolerance = 4;
const getClosestDrawerSnap = (value, snapPoints) => {
  let closest = "closed";
  let closestDistance = Number.POSITIVE_INFINITY;
  Object.entries(snapPoints).forEach(([key, point]) => {
    const distance = Math.abs(value - point);
    if (distance < closestDistance) {
      closest = key;
      closestDistance = distance;
    }
  });
  return closest;
};
export const getDrawerBackdropProgress = (value, openHeight, fullHeight) => {
  if (typeof value !== "number" || typeof openHeight !== "number" || typeof fullHeight !== "number") {
    return 0;
  }
  const range = fullHeight - openHeight;
  if (range <= 0) return 0;
  return Math.max(0, Math.min(1, (value - openHeight) / range));
};
export const useMobileDrawer = ({
  isDesktop,
  openHeight,
  closedHeight,
  fullHeight,
  scrollRef,
  detents = ["closed", "open", "full"],
  desktopSnap = null
}) => {
  const allowFullSnap = detents.includes("full");
  const [drawerSnap, setDrawerSnap] = useState("closed");
  const [drawerHeight, setDrawerHeight] = useState(closedHeight);
  const y = useSpringValue(0);
  const rawDrawerDragRef = useRef(null);
  const openHeightRef = useRef(0);
  const closedHeightRef = useRef(0);
  const fullHeightRef = useRef(0);
  const targetSnapRef = useRef("closed");
  const prevOpenHeightRef = useRef(undefined);
  const prevClosedHeightRef = useRef(undefined);
  const prevFullHeightRef = useRef(undefined);
  const prevAllowFullSnapRef = useRef(undefined);
  const didInitRef = useRef(false);
  const buildSnapPoints = useCallback(({
    closed,
    open,
    full,
    allowFull
  }) => allowFull ? {
    closed,
    open,
    full
  } : {
    closed,
    open
  }, []);
  const getSnapPoints = useCallback(() => buildSnapPoints({
    closed: closedHeightRef.current,
    open: openHeightRef.current,
    full: fullHeightRef.current,
    allowFull: allowFullSnap
  }), [allowFullSnap, buildSnapPoints]);
  const normalizeSnap = useCallback(snap => allowFullSnap || snap !== "full" ? snap : "open", [allowFullSnap]);
  const handleYChange = useCallback(val => {
    const n = typeof val === "number" ? val : Number(val);
    if (Number.isNaN(n)) return;
    setDrawerHeight(n);
  }, []);
  const setDrawerTarget = useCallback(snap_0 => {
    const normalizedSnap = normalizeSnap(snap_0);
    targetSnapRef.current = normalizedSnap;
    setDrawerSnap(normalizedSnap);
  }, [normalizeSnap]);
  useLayoutEffect(() => {
    if (typeof openHeight !== "number" || Number.isNaN(openHeight)) return;
    if (typeof closedHeight !== "number" || Number.isNaN(closedHeight)) return;
    if (typeof fullHeight !== "number" || Number.isNaN(fullHeight)) return;
    const prevOpen = prevOpenHeightRef.current;
    const prevClosed = prevClosedHeightRef.current;
    const prevFull = prevFullHeightRef.current;
    const prevAllowFull = prevAllowFullSnapRef.current;
    openHeightRef.current = openHeight;
    closedHeightRef.current = closedHeight;
    fullHeightRef.current = fullHeight;
    if (isDesktop && desktopSnap) {
      const snap_1 = normalizeSnap(desktopSnap);
      const snapPoints = buildSnapPoints({
        closed: closedHeight,
        open: openHeight,
        full: fullHeight,
        allowFull: allowFullSnap
      });
      const targetHeight = snapPoints[snap_1];
      y.stop();
      y.set(targetHeight);
      setDrawerHeight(targetHeight);
      setDrawerTarget(snap_1);
      didInitRef.current = true;
    } else if (!didInitRef.current || prevOpen === undefined || prevClosed === undefined || prevFull === undefined || prevAllowFull === undefined) {
      y.set(closedHeight);
      setDrawerHeight(closedHeight);
      setDrawerTarget("closed");
      didInitRef.current = true;
    } else if (prevAllowFull !== allowFullSnap || prevOpen !== openHeight || prevClosed !== closedHeight || prevFull !== fullHeight) {
      const nextSnapPoints = buildSnapPoints({
        closed: closedHeight,
        open: openHeight,
        full: fullHeight,
        allowFull: allowFullSnap
      });
      const activeSnap = normalizeSnap(targetSnapRef.current);
      const nextSnapHeight = nextSnapPoints[activeSnap];
      setDrawerTarget(activeSnap);
      y.start({
        to: nextSnapHeight,
        config: config.stiff,
        onChange: handleYChange
      });
    }
    prevOpenHeightRef.current = openHeight;
    prevClosedHeightRef.current = closedHeight;
    prevFullHeightRef.current = fullHeight;
    prevAllowFullSnapRef.current = allowFullSnap;
  }, [allowFullSnap, buildSnapPoints, closedHeight, desktopSnap, fullHeight, handleYChange, isDesktop, normalizeSnap, openHeight, setDrawerTarget, y]);
  const open_0 = useCallback((to = openHeightRef.current) => {
    const nextTo = allowFullSnap ? to : Math.min(to, openHeightRef.current);
    setDrawerTarget(allowFullSnap && nextTo >= fullHeightRef.current - 1 ? "full" : "open");
    y.start({
      to: nextTo,
      config: mobileDrawerOpenSpring,
      onChange: handleYChange
    });
  }, [allowFullSnap, handleYChange, setDrawerTarget, y]);
  const openFull = useCallback((to_0 = fullHeightRef.current) => {
    if (!allowFullSnap) {
      open_0();
      return;
    }
    setDrawerTarget("full");
    y.start({
      to: to_0,
      config: mobileDrawerOpenSpring,
      onChange: handleYChange
    });
  }, [allowFullSnap, handleYChange, open_0, setDrawerTarget, y]);
  const close = useCallback((to_1 = closedHeightRef.current) => {
    setDrawerTarget("closed");
    y.start({
      to: to_1,
      config: mobileDrawerCloseSpring,
      onChange: handleYChange
    });
  }, [handleYChange, setDrawerTarget, y]);
  const getDraggedHeight = useCallback((startHeight, movementY) => {
    const rawHeight = startHeight - movementY;
    const maxHeight = allowFullSnap ? fullHeightRef.current : openHeightRef.current;
    if (rawHeight > maxHeight) {
      return rawHeight > maxHeight ? maxHeight + (rawHeight - maxHeight) * mobileDrawerOverdragResistance : rawHeight;
    }
    if (allowFullSnap && rawHeight > openHeightRef.current) {
      return rawHeight;
    }
    if (rawHeight < closedHeightRef.current) {
      return closedHeightRef.current - (closedHeightRef.current - rawHeight) * mobileDrawerOverdragResistance;
    }
    return rawHeight;
  }, [allowFullSnap]);
  const settleDrawer = useCallback((nextHeight, velocityY, directionY) => {
    const snapPoints_0 = getSnapPoints();
    let nextSnap = getClosestDrawerSnap(nextHeight, snapPoints_0);
    if (velocityY > mobileDrawerSnapVelocityThreshold) {
      if (directionY < 0) {
        nextSnap = allowFullSnap && nextHeight >= snapPoints_0.open + mobileDrawerSnapIntentBuffer ? "full" : nextHeight >= snapPoints_0.closed + mobileDrawerSnapIntentBuffer ? "open" : "closed";
      } else if (directionY > 0) {
        nextSnap = nextHeight <= snapPoints_0.open - mobileDrawerSnapIntentBuffer ? "closed" : "open";
      }
    }
    if (nextSnap === "full") {
      openFull();
    } else if (nextSnap === "open") {
      open_0();
    } else {
      close();
    }
  }, [allowFullSnap, close, getSnapPoints, open_0, openFull]);
  const headerBind = useDrag(({
    last,
    velocity: [, vy],
    direction: [, dy],
    movement: [, my],
    memo = y.get()
  }) => {
    if (isDesktop) return memo;
    const nextHeight_0 = getDraggedHeight(memo, my);
    if (last) {
      settleDrawer(nextHeight_0, vy, dy);
    } else {
      y.set(nextHeight_0);
      handleYChange(nextHeight_0);
    }
    return memo;
  }, {
    filterTaps: true
  }, [getDraggedHeight, handleYChange, isDesktop, settleDrawer]);
  const shouldTransferBodyDrag = useCallback((scroller, movementY_0) => {
    if (!scroller) return false;
    const overflowY = typeof window !== "undefined" ? window.getComputedStyle(scroller).overflowY : "";
    const scrollable = overflowY !== "hidden" && scroller.scrollHeight > scroller.clientHeight + mobileDrawerScrollEdgeTolerance;
    const atTop = scroller.scrollTop <= mobileDrawerScrollEdgeTolerance;
    const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - mobileDrawerScrollEdgeTolerance;
    const maxHeight_0 = allowFullSnap ? fullHeightRef.current : openHeightRef.current;
    const canExpand = allowFullSnap ? y.get() < maxHeight_0 - 1 : true;
    const canCollapse = y.get() > closedHeightRef.current + 1;
    if (!scrollable) {
      return movementY_0 > 0 && canCollapse || movementY_0 < 0 && canExpand;
    }
    if (movementY_0 > 0) {
      return atTop && canCollapse;
    }
    if (movementY_0 < 0) {
      return atBottom && canExpand;
    }
    return false;
  }, [allowFullSnap, y]);
  const setBodyScrollLocked = useCallback(locked => {
    const scroller_0 = scrollRef.current;
    if (!scroller_0) return;
    if (locked) {
      if (!scroller_0.dataset.drawerOverflowY) {
        scroller_0.dataset.drawerOverflowY = scroller_0.style.overflowY || "";
      }
      scroller_0.style.overflowY = "hidden";
      return;
    }
    if ("drawerOverflowY" in scroller_0.dataset) {
      scroller_0.style.overflowY = scroller_0.dataset.drawerOverflowY;
      delete scroller_0.dataset.drawerOverflowY;
    } else {
      scroller_0.style.overflowY = "";
    }
  }, [scrollRef]);
  const getEventClientY = useCallback(event => {
    return event?.touches?.[0]?.clientY ?? event?.changedTouches?.[0]?.clientY ?? event?.clientY ?? null;
  }, []);
  const stopRawDrawerDrag = useCallback(({
    clientY,
    cancel = false
  } = {}) => {
    const state = rawDrawerDragRef.current;
    if (!state) return;
    state.cleanup?.();
    rawDrawerDragRef.current = null;
    setBodyScrollLocked(false);
    if (cancel) return;
    const finalClientY = clientY ?? state.lastClientY ?? state.startClientY;
    const drawerMovementY = finalClientY - state.startClientY;
    const nextHeight_1 = getDraggedHeight(state.startHeight, drawerMovementY);
    const deltaY = finalClientY - (state.prevClientY ?? state.startClientY);
    const deltaT = Math.max(1, (state.lastTs ?? performance.now()) - (state.prevTs ?? state.startTs));
    const velocityY_0 = Math.abs(deltaY) / deltaT;
    const directionY_0 = deltaY === 0 ? 0 : deltaY > 0 ? 1 : -1;
    settleDrawer(nextHeight_1, velocityY_0, directionY_0);
  }, [getDraggedHeight, setBodyScrollLocked, settleDrawer]);
  const startRawDrawerDrag = useCallback(({
    startHeight: startHeight_0,
    startClientY,
    sourceType
  }) => {
    if (startClientY === null) return false;
    stopRawDrawerDrag({
      cancel: true
    });
    setBodyScrollLocked(true);
    const startTs = performance.now();
    const eventNames = sourceType === "pointer" ? {
      move: "pointermove",
      end: "pointerup",
      cancel: "pointercancel"
    } : {
      move: "touchmove",
      end: "touchend",
      cancel: "touchcancel"
    };
    const handleMove = event_0 => {
      const clientY_0 = getEventClientY(event_0);
      if (clientY_0 === null) return;
      if (event_0.cancelable) {
        event_0.preventDefault();
      }
      const state_0 = rawDrawerDragRef.current;
      if (!state_0) return;
      state_0.prevClientY = state_0.lastClientY;
      state_0.prevTs = state_0.lastTs;
      state_0.lastClientY = clientY_0;
      state_0.lastTs = Number(event_0.timeStamp ?? performance.now());
      const drawerMovementY_0 = clientY_0 - state_0.startClientY;
      const nextHeight_2 = getDraggedHeight(state_0.startHeight, drawerMovementY_0);
      y.set(nextHeight_2);
      handleYChange(nextHeight_2);
    };
    const handleEnd = event_1 => {
      stopRawDrawerDrag({
        clientY: getEventClientY(event_1)
      });
    };
    const handleCancel = () => {
      stopRawDrawerDrag({
        cancel: true
      });
    };
    window.addEventListener(eventNames.move, handleMove, {
      passive: false
    });
    window.addEventListener(eventNames.end, handleEnd, {
      passive: false
    });
    window.addEventListener(eventNames.cancel, handleCancel, {
      passive: false
    });
    rawDrawerDragRef.current = {
      startHeight: startHeight_0,
      startClientY,
      startTs,
      prevClientY: startClientY,
      prevTs: startTs,
      lastClientY: startClientY,
      lastTs: startTs,
      cleanup: () => {
        window.removeEventListener(eventNames.move, handleMove);
        window.removeEventListener(eventNames.end, handleEnd);
        window.removeEventListener(eventNames.cancel, handleCancel);
      }
    };
    return true;
  }, [getDraggedHeight, getEventClientY, handleYChange, setBodyScrollLocked, stopRawDrawerDrag, y]);
  const bodyBind = useDrag(({
    event: event_2,
    last: last_0,
    movement: [, my_0],
    velocity: [, vy_0],
    direction: [, dy_0],
    memo: memo_0
  }) => {
    if (isDesktop) return memo_0;
    if (rawDrawerDragRef.current) {
      return memo_0 ?? {
        mode: "raw"
      };
    }
    const scroller_1 = scrollRef.current;
    let nextMemo = memo_0 ?? {
      mode: "scroll",
      startHeight: y.get()
    };
    if (nextMemo.mode !== "drawer") {
      if (Math.abs(my_0) < 6 && !last_0) {
        return nextMemo;
      }
      const shouldTransfer = shouldTransferBodyDrag(scroller_1, my_0);
      if (shouldTransfer) {
        const sourceType_0 = event_2?.type?.startsWith("pointer") ? "pointer" : "touch";
        const startedRaw = startRawDrawerDrag({
          startHeight: y.get(),
          startClientY: getEventClientY(event_2),
          sourceType: sourceType_0
        });
        if (startedRaw) {
          return {
            mode: "raw"
          };
        }
        setBodyScrollLocked(true);
        nextMemo = {
          mode: "drawer",
          startHeight: y.get(),
          transferMovementY: my_0
        };
      } else {
        return nextMemo;
      }
    }
    if (event_2.cancelable) {
      event_2.preventDefault();
    }
    const drawerMovementY_1 = my_0 - (nextMemo.transferMovementY ?? 0);
    const nextHeight_3 = getDraggedHeight(nextMemo.startHeight, drawerMovementY_1);
    if (last_0) {
      setBodyScrollLocked(false);
      settleDrawer(nextHeight_3, vy_0, dy_0);
    } else {
      y.set(nextHeight_3);
      handleYChange(nextHeight_3);
    }
    return nextMemo;
  }, {
    filterTaps: true,
    eventOptions: {
      passive: false
    },
    pointer: {
      touch: true
    }
  }, [getDraggedHeight, handleYChange, getEventClientY, isDesktop, startRawDrawerDrag, setBodyScrollLocked, settleDrawer, shouldTransferBodyDrag, y]);
  useEffect(() => {
    return () => {
      stopRawDrawerDrag({
        cancel: true
      });
      setBodyScrollLocked(false);
    };
  }, [setBodyScrollLocked, stopRawDrawerDrag]);
  return {
    y,
    isOpen: drawerSnap !== "closed",
    isFullOpen: drawerSnap === "full",
    drawerHeight,
    drawerSnap,
    open: open_0,
    openFull,
    close,
    headerBind,
    bodyBind,
    openHeightRef,
    closedHeightRef,
    fullHeightRef
  };
};